import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { logger } from 'shared/lib'
import { getLuminanceHEXColor } from 'shared/lib/getLuminanceHEXColor'
import { type INormalizedApiError } from 'shared/api'
import { type IResponseRcs, RcsApi } from 'entities/Rcs'

const DEFAULT_ACCENT_COLOR = '#068ff9'
const ACCENT_COLOR_ERROR = 'This color is hard to read. Use a higher contrast color.'

export class BrandMediaStore {
  private _disposers: IReactionDisposer[] = []
  private _logoFile: null | File = null
  private _logoUrl = ''
  private _logoError = ''
  private _logoLoading = false
  private _bannerFile: null | File = null
  private _bannerUrl = ''
  private _bannerError = ''
  private _bannerLoading = false
  private _accentColor = DEFAULT_ACCENT_COLOR
  private _accentColorError = ''

  constructor() {
    makeAutoObservable(this)
    this._initLogoReaction()
    this._initBannerReaction()
  }

  get logoFile() {
    return this._logoFile
  }

  get logoUrl() {
    return this._logoUrl
  }

  get logoError() {
    return this._logoError
  }

  get logoLoading() {
    return this._logoLoading
  }

  get hasLogoUrl() {
    return !!this._logoUrl
  }

  get logoName() {
    return this._logoUrl.split('/').pop()
  }

  get logoSubtitle() {
    return this.logoName || '224x224 px, max 50KB'
  }

  get bannerFile() {
    return this._bannerFile
  }

  get bannerUrl() {
    return this._bannerUrl
  }

  get bannerError() {
    return this._bannerError
  }

  get bannerLoading() {
    return this._bannerLoading
  }

  get hasBannerUrl() {
    return !!this._bannerUrl
  }

  get bannerName() {
    return this._bannerUrl.split('/').pop()
  }

  get bannerSubtitle() {
    return this.bannerName || '1440 x 448 px, max 200 KB'
  }

  get accentColor() {
    return this._accentColor
  }

  get accentColorError() {
    return this._accentColorError
  }

  get isValidAccentColor() {
    return getLuminanceHEXColor(this._accentColor) <= 0.5
  }

  setLogoFile = (file: File) => {
    this._logoFile = file
    this._logoUrl = ''
    if (this._logoError) this._logoError = ''
  }

  setBannerFile = (file: File) => {
    this._bannerFile = file
    this._bannerUrl = ''
    if (this._bannerError) this._bannerError = ''
  }

  setAccentColor = (color: string) => {
    this._accentColor = color
    this._accentColorError = this.isValidAccentColor ? '' : ACCENT_COLOR_ERROR
  }

  uploadLogo = async () => {
    if (!this._logoFile) return

    try {
      runInAction(() => {
        this._logoLoading = true
      })

      const formData = new FormData()
      formData.append('file', this._logoFile)

      const {
        data: { url },
      } = await RcsApi.uploadLogo(formData)

      runInAction(() => {
        this._logoUrl = url
        this._logoFile = null
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('file') || 'Failed to upload logo'
        runInAction(() => {
          this._logoError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._logoLoading = false
      })
    }
  }

  uploadBanner = async () => {
    if (!this._bannerFile) return

    try {
      runInAction(() => {
        this._bannerLoading = true
      })

      const formData = new FormData()
      formData.append('file', this._bannerFile)

      const {
        data: { url },
      } = await RcsApi.uploadBanner(formData)

      runInAction(() => {
        this._bannerUrl = url
        this._bannerFile = null
      })
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        const errorMessage = e.errors.getError('file') || 'Failed to upload large banner'
        runInAction(() => {
          this._bannerError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._bannerLoading = false
      })
    }
  }

  removeLogo = async () => {
    const defaultError = 'Failed to remove logo'

    try {
      runInAction(() => {
        this._logoLoading = true
      })

      const {
        data: { deleted },
      } = await RcsApi.removeLogo({ url: this._logoUrl })

      runInAction(() => {
        if (deleted) {
          this._logoUrl = ''
          this._logoFile = null
        } else {
          this._logoError = defaultError
        }
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        const errorMessage = error.errors.getError('file') || defaultError
        runInAction(() => {
          this._logoError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._logoLoading = false
      })
    }
  }

  removeBanner = async () => {
    const defaultError = 'Failed to remove large banner'

    try {
      runInAction(() => {
        this._bannerLoading = true
      })

      const {
        data: { deleted },
      } = await RcsApi.removeBanner({ url: this._bannerUrl })

      runInAction(() => {
        if (deleted) {
          this._bannerUrl = ''
          this._bannerFile = null
        } else {
          this._bannerError = defaultError
        }
      })
    } catch (error) {
      logger.error(error)

      if (error instanceof AxiosError) {
        const errorMessage = error.errors.getError('file') || defaultError
        runInAction(() => {
          this._bannerError = errorMessage
        })
      }
    } finally {
      runInAction(() => {
        this._bannerLoading = false
      })
    }
  }

  validate = () => {
    let hasError = false

    if (!this._logoUrl) {
      this._logoError = 'Upload logo (224×224 px, max 50KB)'
      hasError = true
    }

    if (!this._bannerUrl) {
      this._bannerError = 'Upload banner (1440×448 px, max 200KB)'
      hasError = true
    }

    if (!this.isValidAccentColor) {
      this._accentColorError = ACCENT_COLOR_ERROR
      hasError = true
    }

    return hasError
  }

  init = (data: IResponseRcs) => {
    this._logoUrl = data.logo_path || ''
    this._bannerUrl = data.banner_path || ''
    this._accentColor = data.accent_color || DEFAULT_ACCENT_COLOR
  }

  initErrors = (errors: INormalizedApiError) => {
    this._logoError = errors.getError('logo_path')
    this._bannerError = errors.getError('banner_path')
  }

  reset = () => {
    this._logoFile = null
    this._logoUrl = ''
    this._logoError = ''
    this._logoLoading = false
    this._bannerFile = null
    this._bannerUrl = ''
    this._bannerError = ''
    this._bannerLoading = false
    this._accentColor = DEFAULT_ACCENT_COLOR
    this._accentColorError = ''
  }

  dispose = () => {
    this._disposers.forEach((dispose) => dispose())
    this._disposers = []
  }

  private _initLogoReaction = () => {
    this._disposers.push(
      reaction(
        () => this._logoFile,
        (value) => {
          if (value) this.uploadLogo()
        }
      )
    )
  }

  private _initBannerReaction = () => {
    this._disposers.push(
      reaction(
        () => this._bannerFile,
        (value) => {
          if (value) this.uploadBanner()
        }
      )
    )
  }
}
